import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ImageGallery } from '@/components/media/ImageGallery';
import { usePreferencesStore } from '@/store';
import { 
  ExternalLink, 
  Twitter, 
  Rss, 
  ChevronDown, 
  ChevronUp,
  Bookmark,
  Copy,
  CheckCircle2,
  Globe,
  Newspaper
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/utils';
import type { UnifiedContentItem } from '@/types';

interface UnifiedContentCardProps {
  item: UnifiedContentItem;
  isNew?: boolean;
}

export function UnifiedContentCard({ item, isNew = false }: UnifiedContentCardProps) {
  const { preferences } = usePreferencesStore();
  const defaultExpand = preferences?.default_expand_feed === 1;
  
  const [showTranslation, setShowTranslation] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpand);
  const [isCopied, setIsCopied] = useState(false);
  
  const isTweet = item.type === 'tweet';
  const isRSS = item.type === 'rss';
  
  // Get display name
  const displayName = isTweet 
    ? (item.author?.display_name || item.author?.username)
    : item.source?.name;
  
  const username = isTweet ? item.author?.username : null;
  const isVerified = isTweet ? item.author?.verified : false;
  
  // Get category and tags
  const category = item.enrichment?.category || 'other';
  const tags = item.enrichment?.tags || [];
  const sentiment = item.enrichment?.sentiment;
  
  // Get content to display
  const displayContent = showTranslation && item.translation
    ? item.translation.translated_text
    : item.content;
  
  const shouldTruncate = displayContent.length > 300 && !isExpanded;
  const truncatedContent = shouldTruncate 
    ? displayContent.slice(0, 300) + '...'
    : displayContent;
  
  // Category colors
  const categoryColors: Record<string, string> = {
    news: 'bg-blue-500/10 text-blue-600 border-blue-200',
    opinion: 'bg-purple-500/10 text-purple-600 border-purple-200',
    analysis: 'bg-amber-500/10 text-amber-600 border-amber-200',
    humor: 'bg-pink-500/10 text-pink-600 border-pink-200',
    discussion: 'bg-green-500/10 text-green-600 border-green-200',
    promotion: 'bg-orange-500/10 text-orange-600 border-orange-200',
    technology: 'bg-cyan-500/10 text-cyan-600 border-cyan-200',
    business: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
    other: 'bg-gray-500/10 text-gray-600 border-gray-200',
  };
  
  // Sentiment colors
  const sentimentColors: Record<string, string> = {
    positive: 'bg-green-500 text-white',
    negative: 'bg-red-500 text-white',
    neutral: 'bg-gray-500 text-white',
    mixed: 'bg-yellow-500 text-white',
  };
  
  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };
  
  const handleCopy = () => {
    const title = item.title || (isTweet ? `${item.author?.display_name || item.author?.username} 的推文` : '内容');
    const content = item.content || '';
    const link = item.url || '';
    
    const textToCopy = `${title}

${content}

链接: ${link}`;
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };
  
  return (
    <Card 
      className={cn(
        "transition-all duration-300",
        isNew && "ring-2 ring-primary/30 animate-in slide-in-from-top-2",
        "hover:shadow-md"
      )}
    >
      {/* Header */}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <Avatar className="h-10 w-10">
              <AvatarFallback className={cn(
                isTweet ? "bg-blue-500/10 text-blue-600" : "bg-orange-500/10 text-orange-600"
              )}>
                {isTweet ? <Twitter className="h-5 w-5" /> : <Rss className="h-5 w-5" />}
              </AvatarFallback>
            </Avatar>
            
            {/* Author/Source info */}
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-sm">{displayName}</span>
                {isVerified && (
                  <CheckCircle2 className="h-4 w-4 text-blue-500 fill-blue-500" />
                )}
              </div>
              {username && (
                <span className="text-xs text-muted-foreground">@{username}</span>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{formatRelativeTime(item.created_at)}</span>
                {isRSS && item.source?.category && (
                  <>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      {item.source.category}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Type indicator */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn("text-xs", categoryColors[category])}>
              {isRSS && <Newspaper className="h-3 w-3 mr-1" />}
              {category}
            </Badge>
            {sentiment && (
              <Badge className={cn("text-xs", sentimentColors[sentiment])}>
                {sentiment}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Title for RSS */}
        {isRSS && item.title && (
          <h3 className="font-semibold text-lg mt-2 leading-tight">
            {item.title}
          </h3>
        )}
      </CardHeader>
      
      {/* Content */}
      <CardContent className="pt-0">
        {/* Text content */}
        <div className="text-sm leading-relaxed whitespace-pre-wrap">
          {truncatedContent}
        </div>
        
        {/* Expand/Collapse button */}
        {displayContent.length > 300 && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-8 text-xs"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                收起
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                展开全文
              </>
            )}
          </Button>
        )}
        
        {/* Translation toggle */}
        {item.translation && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-8 text-xs"
            onClick={() => setShowTranslation(!showTranslation)}
          >
            {showTranslation ? '显示原文' : `查看翻译 (${item.translation.target_language})`}
          </Button>
        )}
        
        {/* Media gallery */}
        {item.media_urls && item.media_urls.length > 0 && (
          <div className="mt-3">
            <ImageGallery 
              mediaUrls={item.media_urls} 
              alt={item.title || 'Content image'}
            />
          </div>
        )}
        
        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tags.slice(0, 5).map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                #{tag}
              </Badge>
            ))}
            {tags.length > 5 && (
              <Badge variant="secondary" className="text-xs">
                +{tags.length - 5}
              </Badge>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 text-xs",
                isBookmarked && "text-primary"
              )}
              onClick={handleBookmark}
            >
              <Bookmark className={cn("h-4 w-4 mr-1", isBookmarked && "fill-current")} />
              收藏
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={handleCopy}
            >
              {isCopied ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1 text-green-500" />
                  已复制
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  复制
                </>
              )}
            </Button>
          </div>
          
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              {isTweet ? '在 X 查看' : '阅读原文'}
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
